import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpFromLine, Upload, Wand2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import type {
  ChangeMatrixCell,
  FunctionPartMatrix,
  MatrixCellUpdateRequest,
} from '../../../types/drbfm';
import ChangeMatrixGrid from './ChangeMatrixGrid';
import FunctionRequirementTable from './FunctionRequirementTable';
import MatrixCellDrawer from './MatrixCellDrawer';
import PartTreeTable from './PartTreeTable';

interface FunctionPartMatrixWorkbenchProps {
  matrix?: FunctionPartMatrix | null;
  isLoading: boolean;
  onImport: (file: File) => void | Promise<void>;
  onPrefill: () => void | Promise<void>;
  onUpdateCell: (
    cellId: string,
    patch: MatrixCellUpdateRequest,
  ) => void | Promise<void>;
  onPromote: () => void | Promise<void>;
}

const FunctionPartMatrixWorkbench: React.FC<FunctionPartMatrixWorkbenchProps> = ({
  matrix,
  isLoading,
  onImport,
  onPrefill,
  onUpdateCell,
  onPromote,
}) => {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);
  const [selectedCell, setSelectedCell] = useState<ChangeMatrixCell | null>(null);
  const coverage = matrix?.coverage;
  const reviewed = (coverage?.totalCells || 0) - (coverage?.uncheckedCells || 0);
  const progress = coverage?.totalCells
    ? Math.round((reviewed / coverage.totalCells) * 100)
    : 0;
  const selectedPart = matrix?.partNodes.find(
    (part) => part.id === selectedCell?.partNodeId,
  );
  const selectedAxis = matrix?.axes.find(
    (axis) => axis.id === selectedCell?.axisId,
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col justify-between gap-2 md:flex-row">
        <div className="flex flex-col">
          <h6 className="text-lg font-semibold text-foreground">
            {t('drbfm.workflow.matrix.workbenchTitle', '機能一覧 × 部品ツリー 変化点レビュー')}
          </h6>
          <p className="text-sm text-muted-foreground">
            {t(
              'drbfm.workflow.matrix.workbenchDescription',
              '全セルを確認してから、変更ありセルだけをDRBFM設計変更へ送ります。',
            )}
          </p>
        </div>
        <div className="flex flex-row flex-wrap gap-2">
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void onImport(file);
              event.target.value = '';
            }}
          />
          <Button
            variant="outline"
            disabled={isLoading}
            onClick={() => fileInput.current?.click()}
          >
            <Upload className="h-4 w-4" />
            {t('drbfm.workflow.matrix.excelImport', 'Excel取込')}
          </Button>
          <Button
            variant="outline"
            disabled={isLoading || !matrix?.cells.length}
            onClick={() => void onPrefill()}
          >
            <Wand2 className="h-4 w-4" />
            {t('drbfm.workflow.matrix.aiDraft', 'AI下書き')}
          </Button>
          <Button
            disabled={isLoading || !coverage?.promotionReady}
            onClick={() => void onPromote()}
          >
            <ArrowUpFromLine className="h-4 w-4" />
            {t('drbfm.workflow.progress.promoteToDesignChange', '設計変更へ昇格')}
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="h-1 w-full overflow-hidden rounded-full bg-primary/20">
          <div className="h-full w-full animate-pulse bg-primary" />
        </div>
      )}

      {!matrix?.cells.length ? (
        <Alert variant="info">
          <AlertDescription>
            {t(
              'drbfm.workflow.matrix.importHint',
              '「機能一覧」と、部品階層（「部品ツリー」または「階層図」）、変化点（「変化点抽出マトリクス」または「変更点・変化点」）を含む .xlsx を取り込んでください。',
            )}
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <div className="flex flex-row flex-wrap gap-2">
            <Badge variant="secondary">
              {t('drbfm.workflow.matrix.reviewedCount', '確認 {{reviewed}}/{{total}}', {
                reviewed,
                total: coverage?.totalCells || 0,
              })}
            </Badge>
            <Badge variant={(coverage?.uncheckedCells || 0) > 0 ? 'warning' : 'success'}>
              {t('drbfm.workflow.matrix.uncheckedCount', '未確認 {{count}}', {
                count: coverage?.uncheckedCells || 0,
              })}
            </Badge>
            <Badge variant="success">
              {t('drbfm.workflow.matrix.changedCount', '変更 {{count}}', {
                count: coverage?.changedCells || 0,
              })}
            </Badge>
            <Badge variant="destructive">
              {t('drbfm.workflow.matrix.importantChangedCount', '重要変化 {{count}}', {
                count: coverage?.importantChangedCells || 0,
              })}
            </Badge>
            {(coverage?.importantWithoutRequirements || 0) > 0 && (
              <Badge variant="warning">
                {t('drbfm.workflow.matrix.importantWithoutRequirementsCount', '要求未指定 {{count}}', {
                  count: coverage?.importantWithoutRequirements || 0,
                })}
              </Badge>
            )}
            <Badge variant="secondary">
              {t('drbfm.workflow.matrix.unknownCount', '不明 {{count}}', {
                count: coverage?.unknownCells || 0,
              })}
            </Badge>
          </div>
          <Progress value={progress} />
          {!coverage?.promotionReady && (
            <Alert variant="warning">
              <AlertDescription>
                {t(
                  'drbfm.workflow.matrix.promotionRequirements',
                  '昇格には全セルの確認と、変更ありセルが1件以上必要です。重要変化には影響要求も指定します。',
                )}
              </AlertDescription>
            </Alert>
          )}
          <ChangeMatrixGrid
            parts={matrix.partNodes}
            axes={matrix.axes}
            cells={matrix.cells}
            onSelectCell={setSelectedCell}
          />
          <Accordion type="multiple">
            <AccordionItem value="requirements">
              <AccordionTrigger>
                {t('drbfm.workflow.matrix.requirementsCount', '機能要求（{{count}}件）', {
                  count: matrix.requirements.length,
                })}
              </AccordionTrigger>
              <AccordionContent>
                <FunctionRequirementTable requirements={matrix.requirements} />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="parts">
              <AccordionTrigger>
                {t('drbfm.workflow.matrix.partTreeCount', '部品ツリー（{{count}}件）', {
                  count: matrix.partNodes.length,
                })}
              </AccordionTrigger>
              <AccordionContent>
                <PartTreeTable parts={matrix.partNodes} />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </>
      )}

      <MatrixCellDrawer
        open={Boolean(selectedCell)}
        cell={selectedCell}
        part={selectedPart}
        axis={selectedAxis}
        requirements={matrix?.requirements || []}
        isLoading={isLoading}
        onClose={() => setSelectedCell(null)}
        onSave={async (cellId, patch) => {
          await onUpdateCell(cellId, patch);
          setSelectedCell(null);
        }}
      />
    </div>
  );
};

export default FunctionPartMatrixWorkbench;
